import React from 'react';
import { Wallet, TrendingUp, TrendingDown, Lock, DollarSign, Info, Layers, Activity, Package } from 'lucide-react';

interface WalletBreakdownCardProps {
  totalBalance: number;
  usedMargin: number;
  futuresUsedMargin: number;
  propUsedMargin: number;
  futuresOrdersReserved: number;
  propOrdersReserved: number;
  unrealizedPnl: number;
  availableBalance: number;
  robotAllocatedBalance: number;
  stakedAmount: number;
  loading?: boolean;
  showDetails?: boolean;
}

const cardShellClass = 'rounded-2xl border border-purple-500/30 bg-gradient-to-br from-blue-500/24 via-indigo-500/18 to-purple-500/16 p-6 shadow-2xl shadow-purple-500/10 backdrop-blur-sm';
const iconBadgeClass = 'flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/25';
const statRowCardClass = 'rounded-xl border border-purple-500/25 bg-gradient-to-br from-indigo-500/12 via-purple-500/12 to-fuchsia-500/12 p-4';
const mutedLabelClass = 'text-slate-300';
const mutedMetaClass = 'text-slate-400';
const infoNoteClass = 'flex items-start gap-2 rounded-xl border border-purple-500/30 bg-gradient-to-r from-indigo-500/12 via-purple-500/12 to-fuchsia-500/14 p-3';

const WalletBreakdownCard: React.FC<WalletBreakdownCardProps> = ({
  totalBalance,
  usedMargin,
  futuresUsedMargin,
  propUsedMargin,
  futuresOrdersReserved,
  propOrdersReserved,
  unrealizedPnl,
  availableBalance,
  robotAllocatedBalance,
  stakedAmount,
  loading = false,
  showDetails = true
}) => {
  const formatCurrency = (amount: number) => {
    if (isNaN(amount) || !isFinite(amount)) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className={cardShellClass}>
        <div className="flex items-center gap-3 mb-4">
          <div className={iconBadgeClass}>
            <Wallet size={20} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">Wallet Breakdown</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-3/4 rounded bg-purple-500/20"></div>
          <div className="h-4 w-1/2 rounded bg-purple-500/18"></div>
          <div className="h-4 w-2/3 rounded bg-purple-500/16"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={cardShellClass}>
      <div className="flex items-center gap-3 mb-6">
        <div className={iconBadgeClass}>
          <Wallet size={20} className="text-white" />
        </div>
        <h3 className="text-lg font-semibold text-white">Wallet Breakdown</h3>
      </div>

      <div className="space-y-4">
        {/* Total Balance */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <DollarSign size={16} className="text-purple-300" />
            <span className={mutedLabelClass}>Total Balance</span>
          </div>
          <span className="text-white font-bold text-lg">{formatCurrency(totalBalance)}</span>
        </div>

        {showDetails && (
          <>
            {/* Used Margin with breakdown */}
            {usedMargin > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Lock size={16} className="text-purple-300" />
                    <span className={mutedLabelClass}>Used Margin</span>
                  </div>
                  <span className="text-purple-300 font-medium">{formatCurrency(usedMargin)}</span>
                </div>
                
                {/* Margin breakdown */}
                <div className="ml-6 space-y-1">
                  {futuresUsedMargin > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Activity size={12} className="text-purple-200" />
                        <span className={mutedMetaClass}>Futures Positions</span>
                      </div>
                      <span className="text-purple-200">{formatCurrency(futuresUsedMargin)}</span>
                    </div>
                  )}
                  {propUsedMargin > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Activity size={12} className="text-purple-200" />
                        <span className={mutedMetaClass}>Prop Positions</span>
                      </div>
                      <span className="text-purple-200">{formatCurrency(propUsedMargin)}</span>
                    </div>
                  )}
                  {futuresOrdersReserved > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Package size={12} className="text-purple-200" />
                        <span className={mutedMetaClass}>Futures Orders</span>
                      </div>
                      <span className="text-purple-200">{formatCurrency(futuresOrdersReserved)}</span>
                    </div>
                  )}
                  {propOrdersReserved > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Package size={12} className="text-purple-200" />
                        <span className={mutedMetaClass}>Prop Orders</span>
                      </div>
                      <span className="text-purple-200">{formatCurrency(propOrdersReserved)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Unrealized PnL */}
            {unrealizedPnl !== 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {unrealizedPnl >= 0 ? (
                    <TrendingUp size={16} className="text-emerald-400" />
                  ) : (
                    <TrendingDown size={16} className="text-red-400" />
                  )}
                  <span className={mutedLabelClass}>Unrealized PnL</span>
                </div>
                <span className={`font-medium ${unrealizedPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {unrealizedPnl >= 0 ? '+' : ''}{formatCurrency(unrealizedPnl)}
                </span>
              </div>
            )}

            {/* Robot Allocated Balance */}
            {robotAllocatedBalance > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Lock size={16} className="text-purple-400" />
                  <span className={mutedLabelClass}>Reserved (Robot)</span>
                </div>
                <span className="text-purple-400 font-medium">{formatCurrency(robotAllocatedBalance)}</span>
              </div>
            )}

            {/* Staked Amount */}
            {stakedAmount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Layers size={16} className="text-indigo-400" />
                  <span className={mutedLabelClass}>Staked Assets</span>
                </div>
                <span className="text-indigo-400 font-medium">{formatCurrency(stakedAmount)}</span>
              </div>
            )}

            {/* Divider */}
            <div className="my-4 border-t border-purple-500/20"></div>
          </>
        )}

        {/* Available Balance */}
        <div className={`flex justify-between items-center ${statRowCardClass}`}>
          <div className="flex items-center gap-2">
            <Wallet size={16} className="text-purple-300" />
            <span className="text-white font-semibold">Available Balance</span>
          </div>
          <span className="text-purple-300 font-bold text-xl">{formatCurrency(availableBalance)}</span>
        </div>

        {/* Info note */}
        <div className={infoNoteClass}>
          <Info size={14} className="mt-0.5 flex-shrink-0 text-purple-300" />
          <div className="text-xs text-purple-200">
            <p className="font-medium mb-1">Available Balance</p>
            <p>This is the amount you can withdraw, stake, or use for trading. Used margin, reserved funds (robot), and staked assets are deducted from your total balance.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletBreakdownCard;
